import { useMsal } from "@azure/msal-react";
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getAccessToken } from "../services/authService";
import { listFolders } from "../services/fileService";
import { listAvailableSourceFolders, listSiteLibraries } from "../services/sourceFolderService";
import { AvailableSource } from "../types";
import { useSourceRequestGuard } from "./useSourceRequestGuard";

interface FolderNode {
  id: string;
  name: string;
  driveId?: string;
  itemId?: string;
  parentId?: string;
  isLoaded?: boolean;
  isLoading?: boolean;
  children?: string[];
  nextSkipToken?: string | null;
  webUrl?: string;
}

interface FileBrowserState {
  availableSources: AvailableSource[];
  selectedSource: AvailableSource | null;
  setSelectedSourceId: Dispatch<SetStateAction<string | null>>;
  appFolder: { driveId: string; itemId: string; name: string; webUrl?: string | null } | null;
  appFolderError: string | null;
  sourceWarning: string | null;
  isAppFolderLoading: boolean;
  isFoldersLoading: boolean;
  nodes: Record<string, FolderNode>;
  nodesRef: MutableRefObject<Record<string, FolderNode>>;
  openItems: Set<string>;
  setOpenItems: Dispatch<SetStateAction<Set<string>>>;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
  sourceRequests: ReturnType<typeof useSourceRequestGuard>;
  loadChildren: (
    nodeId: string,
    append?: boolean,
    driveId?: string,
    itemId?: string
  ) => Promise<void>;
  reloadSources: () => Promise<void>;
}

const INCOMPLETE_SOURCES_MESSAGE = "Some shared locations could not be loaded.";

/** Loads selectable file locations and folder trees for both extraction workflows. */
export function useFileBrowser(onSourceChange: () => void): FileBrowserState {
  const { instance } = useMsal();
  const sourceRequests = useSourceRequestGuard();
  const sourceListRequests = useSourceRequestGuard();
  const [availableSources, setAvailableSources] = useState<AvailableSource[]>([]);
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [appFolderError, setAppFolderError] = useState<string | null>(null);
  const [sourceWarning, setSourceWarning] = useState<string | null>(null);
  const [isAppFolderLoading, setIsAppFolderLoading] = useState(true);
  const [isFoldersLoading, setIsFoldersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nodes, setNodes] = useState<Record<string, FolderNode>>({});
  const nodesRef = useRef(nodes);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const selectedSource = useMemo(
    () => availableSources.find((source) => source.id === selectedSourceId) ?? null,
    [availableSources, selectedSourceId]
  );
  const selectedSourceRef = useRef(selectedSource);
  const appFolder = selectedSource
    ? {
        driveId: selectedSource.drive_id || "",
        itemId: selectedSource.item_id || "",
        name: selectedSource.name,
        webUrl: selectedSource.web_url,
      }
    : null;

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  useEffect(() => {
    selectedSourceRef.current = selectedSource;
  }, [selectedSource]);

  const reloadSources = useCallback(async (): Promise<void> => {
    sourceListRequests.invalidate();
    const isCurrent = sourceListRequests.capture();
    setIsAppFolderLoading(true);
    setAppFolderError(null);
    try {
      const token = await getAccessToken(instance);
      const response = await listAvailableSourceFolders(token);
      if (!isCurrent()) return;
      const sources = (response.sources || []).filter((source) =>
        source.kind === "shared_site"
          ? Boolean(source.id)
          : Boolean(source.drive_id && source.item_id)
      );
      setSourceWarning(response.shared_sources_incomplete ? INCOMPLETE_SOURCES_MESSAGE : null);
      if (sources.length === 0) {
        throw new Error(
          response.shared_sources_incomplete
            ? INCOMPLETE_SOURCES_MESSAGE
            : "No storage sources are available. Run OneDrive setup or ask an admin to register SharePoint."
        );
      }
      setAvailableSources(sources);
      setSelectedSourceId((current) =>
        sources.some((source) => source.id === current)
          ? current
          : (sources.find((source) => source.kind === "app_folder") ?? sources[0]).id
      );
    } catch (failure: unknown) {
      if (!isCurrent()) return;
      setAvailableSources([]);
      setSelectedSourceId(null);
      setSourceWarning(null);
      setAppFolderError(
        failure instanceof Error ? failure.message : "Could not load storage sources."
      );
    } finally {
      if (isCurrent()) setIsAppFolderLoading(false);
    }
  }, [instance, sourceListRequests]);

  useEffect(() => {
    void reloadSources();
    return sourceListRequests.invalidate;
  }, [reloadSources, sourceListRequests]);

  const loadChildren = useCallback(
    async (nodeId: string, append = false, driveId?: string, itemId?: string): Promise<void> => {
      const isCurrent = sourceRequests.capture();
      setNodes((current) => ({
        ...current,
        [nodeId]: { ...(current[nodeId] || { id: nodeId, name: "" }), isLoading: true },
      }));
      setError(null);
      try {
        const token = await getAccessToken(instance);
        const snapshot = nodesRef.current[nodeId];
        const targetDrive = driveId ?? snapshot?.driveId;
        const targetItem = itemId ?? snapshot?.itemId;
        if (!targetDrive || !targetItem)
          throw new Error("Missing folder identifiers. Choose the source again.");
        const result = await listFolders(
          {
            parent_drive_id: targetDrive,
            parent_item_id: targetItem,
            top: 50,
            skip_token: append ? snapshot?.nextSkipToken || undefined : undefined,
          },
          token
        );
        if (!isCurrent()) return;
        setNodes((current) => {
          const parent = current[nodeId] || { id: nodeId, name: "" };
          const children = Object.fromEntries(
            (result.items || []).map((folder) => {
              const id = `${folder.driveId}:${folder.id}`;
              return [
                id,
                current[id] || {
                  id,
                  name: folder.name,
                  driveId: folder.driveId,
                  itemId: folder.id,
                  parentId: nodeId,
                  isLoaded: false,
                  webUrl: folder.webUrl,
                },
              ];
            })
          );
          return {
            ...current,
            ...children,
            [nodeId]: {
              ...parent,
              children: append
                ? [...(parent.children || []), ...Object.keys(children)]
                : Object.keys(children),
              isLoaded: true,
              isLoading: false,
              nextSkipToken: result.nextSkipToken || null,
            },
          };
        });
      } catch (failure: unknown) {
        if (!isCurrent()) return;
        setError(failure instanceof Error ? failure.message : "Failed to load folders.");
        setNodes((current) => ({
          ...current,
          [nodeId]: { ...current[nodeId], isLoading: false },
        }));
      }
    },
    [instance, sourceRequests]
  );

  const loadSiteLibraries = useCallback(
    async (sourceId: string): Promise<void> => {
      const isCurrent = sourceRequests.capture();
      const token = await getAccessToken(instance);
      const response = await listSiteLibraries(token, sourceId);
      if (!isCurrent()) return;
      const libraries = Object.fromEntries(
        (response.sources || [])
          .filter((source) => source.drive_id && source.item_id)
          .map((library) => {
            const id = `${library.drive_id}:${library.item_id}`;
            return [
              id,
              {
                id,
                name: library.name,
                driveId: library.drive_id || undefined,
                itemId: library.item_id || undefined,
                parentId: "root",
                isLoaded: false,
                webUrl: library.web_url || undefined,
              },
            ];
          })
      );
      setNodes((current) => ({
        ...current,
        ...libraries,
        root: {
          ...current.root,
          children: Object.keys(libraries),
          isLoaded: true,
          isLoading: false,
        },
      }));
    },
    [instance, sourceRequests]
  );

  useEffect(() => {
    const source = selectedSourceRef.current;
    sourceRequests.invalidate();
    setError(null);
    onSourceChange();
    if (!source) {
      setNodes({});
      setIsFoldersLoading(false);
      return sourceRequests.invalidate;
    }
    const isCurrent = sourceRequests.capture();
    setNodes({
      root: {
        id: "root",
        name: source.name,
        driveId: source.drive_id || undefined,
        itemId: source.item_id || undefined,
        isLoaded: false,
        isLoading: true,
        nextSkipToken: null,
        webUrl: source.web_url || undefined,
      },
    });
    setOpenItems(new Set(["root"]));
    setIsFoldersLoading(true);
    const loadRoot =
      source.kind === "shared_site"
        ? loadSiteLibraries(source.id)
        : loadChildren("root", false, source.drive_id || undefined, source.item_id || undefined);
    void loadRoot
      .catch((failure: unknown) => {
        if (!isCurrent()) return;
        setError(failure instanceof Error ? failure.message : "Failed to load site libraries.");
        setNodes((current) => ({ ...current, root: { ...current.root, isLoading: false } }));
      })
      .finally(() => {
        if (isCurrent()) setIsFoldersLoading(false);
      });
    return sourceRequests.invalidate;
  }, [
    loadChildren,
    loadSiteLibraries,
    onSourceChange,
    selectedSource?.id,
    selectedSource?.kind,
    selectedSource?.drive_id,
    selectedSource?.item_id,
    selectedSource?.site_id,
    sourceRequests,
  ]);

  return {
    availableSources,
    selectedSource,
    setSelectedSourceId,
    appFolder,
    appFolderError,
    sourceWarning,
    isAppFolderLoading,
    isFoldersLoading,
    nodes,
    nodesRef,
    openItems,
    setOpenItems,
    error,
    setError,
    sourceRequests,
    loadChildren,
    reloadSources,
  };
}
